import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useState, useMemo, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import imageHelper from '@utils/imageHelper';
import textHelper from '@utils/textHelper';
import dateHelper from '@utils/dates';
import P1 from '@base/P1/P1';
import Avatar from '@base/Avatar/Avatar';
import H6 from '@base/H6/H6';
import { toggleLoginModal } from '@actions/auth';
import { useDispatchToStore } from '@hooks/reduxHooks';
// import tagHelper from '@utils/tagHelper';
import { Context as IsMobileContext } from '../../../Context/IsMobileContext';
import {
  Spacings,
  Fonts,
  TextColors,
  BgColors,
  BorderColors
} from '../../../../styleConstants';
import { buttonTypes, getIsLive } from './scheduleHelper';
// import { tagColorMap, eventColorMap } from '../colormap';
import CardIcon from './CardIcon';
import { Live, Play } from './CardIcons';

const Wrapper = styled.div`
  overflow: hidden;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'not-allowed')};
  display: flex;
  flex-direction: column;
`;

const TagsWrapper = styled.div`
  display: grid;
  align-items: center;
  grid-template-rows: 1fr / max-content 1fr max-content;
  grid-template-columns: ${({ $isLive }) =>
    `${$isLive ? 'max-content' : ''} 1fr max-content;`};
  grid-column-gap: ${Spacings.SPACING_2B};
`;

const Tag = styled(P1)`
  display: inline-block;
  padding: ${Spacings.SPACING_2} ${Spacings.SPACING_2B};
  border-radius: ${Spacings.SPACING_1B};
  background-color: ${({ $bgColor }) => $bgColor};
`;

const LiveTag = styled(H6)`
  color: ${TextColors.WHITE};
  padding: ${Spacings.SPACING_2} ${Spacings.SPACING_2B};
  border-radius: ${Spacings.SPACING_1B};
  background-color: ${BgColors.LIVE};
`;

const Title = styled.div`
  margin-top: ${Spacings.SPACING_2B};
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.H4};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: #1f2432;
  min-height: ${Spacings.SPACING_14B};
`;

const AuthorInfo = styled.div`
  padding-top: ${Spacings.SPACING_6B};
  display: grid;
  grid-template: 1fr / max-content 1fr ${Spacings.SPACING_12B};
  grid-column-gap: ${Spacings.SPACING_3B};
  align-items: center;
  margin-top: auto;
`;

const AvatarContainer = styled.div`
  max-width: ${Spacings.SPACING_22B};
  position: relative;
  width: ${({ $count }) => `${40 + 24 * ($count - 1)}px`};
  height: ${Spacings.SPACING_10B};
`;

const StyledAvatar = styled(Avatar)`
  position: absolute;
  border: ${Spacings.SPACING_2} solid ${BorderColors.WHITE};
  z-index: ${({ $index }) => 3 - $index};
  left: ${({ $index }) => `${24 * $index}px`};
  top: ${Spacings.SPACING_0B};
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'not-allowed')};
`;

const AuthorName = styled(P1)`
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: #7b8ab5;
`;

const Duration = styled(AuthorName)`
  text-align: right;
`;

const IconContainer = styled.div`
  position: relative;
  height: 100%;
`;

const StarIcon = styled.img`
  user-drag: none;
`;

const getFullName = (author) => {
  return `${
    author.first_name.charAt(0).toUpperCase() + author.first_name.slice(1)
  } ${author.last_name.charAt(0).toUpperCase() + author.last_name.slice(1)}`;
};

const ScheduleCard = ({ data, className }) => {
  const isMobileDevice = useContext(IsMobileContext);
  const {
    video_duration: lessonDuration,
    programme,
    finished_at,
    is_live: live,
    live_at,
    permalink,
    relative_link: relativeLink,
    youtubeLink,
    isMustWatch,
    isYoutube
  } = data;
  const { authors } = programme;
  const [isLive, setIsLive] = useState(live);
  useEffect(() => {
    setIsLive(getIsLive(data));
  }, [data]);
  const finishedAt =
    finished_at ?? dateHelper.addTime(new Date(live_at), lessonDuration, 's');
  const hasClassEnded = finishedAt
    ? dateHelper.compareDates(finishedAt, new Date()) === 1
    : false;
  const buttonType = useMemo(() => {
    if (isLive) {
      return buttonTypes.live;
    }
    if (hasClassEnded) {
      return buttonTypes.completed;
    }
    return -1;
  }, [hasClassEnded, isLive]);
  const [showButton, setShowButton] = useState(false);
  const onMouseEnter = () => {
    setShowButton(true);
  };

  const onMouseLeave = () => {
    setShowButton(false);
  };
  const staticIcon = useMemo(() => {
    if (isMobileDevice) {
      if (isLive) return Live;
      if (hasClassEnded) return Play;
    }
    if (isMustWatch && !isLive) {
      return <StarIcon src={imageHelper.getImage('summit/star.svg')} />;
    }
  }, [hasClassEnded, isLive, isMobileDevice, isMustWatch]);

  const isLoggedIn = useSelector(({ auth }) => auth.isLoggedIn);

  const topicGroupColorMap = useSelector(
    ({ summit }) => summit.topicGroupColorMap || {}
  );

  // const isClickable = isLive || hasClassEnded;
  const isClickable = !!permalink;

  const authorsAvatars = useMemo(() => {
    return (
      <AvatarContainer $count={Math.min(authors.length, 3)}>
        {authors.slice(0, 3).map(({ author_details: author }, index) => (
          <StyledAvatar
            key={author.username}
            size={Spacings.SPACING_10B}
            src={author.avatar}
            $index={index}
            $isClickable={isClickable}
          />
        ))}
      </AvatarContainer>
    );
  }, [authors, isClickable]);

  const dispatch = useDispatchToStore();
  const onClick = isClickable
    ? () => {
        if (!isLoggedIn) {
          if (isMobileDevice) window.location.href = '/login';
          else dispatch(toggleLoginModal(true));
        } else window.open(isYoutube ? youtubeLink : relativeLink, '_blank');
      }
    : undefined;

  const colors =
    topicGroupColorMap[programme.topic_groups[0].uid] ||
    topicGroupColorMap[programme.topic_groups[1].uid];

  return (
    <Wrapper
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      $isClickable={isClickable}
      className={className}
    >
      <TagsWrapper $isLive={isLive}>
        {isLive && <LiveTag>LIVE</LiveTag>}
        <div>
          <Tag color={colors?.textColor} $bgColor={colors?.bgColor}>
            {programme.topic_groups[0].title}
          </Tag>
        </div>
        <Duration>
          {dateHelper.getMinutesValueFromSeconds(lessonDuration)}
        </Duration>
      </TagsWrapper>
      <Title>{programme.name}</Title>
      <AuthorInfo>
        {authorsAvatars}
        <AuthorName>
          {`${getFullName(authors[0])}${
            authors.length > 1
              ? ` and ${textHelper.pluralize(authors.length - 1, 'other')}`
              : ''
          }`}
        </AuthorName>
        <IconContainer>
          {staticIcon}
          {!isMobileDevice && (
            <CardIcon showButton={showButton} buttonType={buttonType} />
          )}
        </IconContainer>
      </AuthorInfo>
    </Wrapper>
  );
};

ScheduleCard.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  className: PropTypes.string
};

ScheduleCard.defaultProps = {
  className: ''
};

export default ScheduleCard;
